/**
 * @fileoverview SweetAlert2 Utility Functions - Centralized alert and notification system
 * 
 * This module provides a consistent, themed interface for user notifications throughout
 * the Harry Potter Quiz App. All alerts use a dark theme that matches the application's
 * magical aesthetic.
 * 
 * Features:
 * - Consistent dark theme styling across all alert types
 * - Success, error, warning, and confirmation dialogs
 * - Centralized theming for easy maintenance
 * - Promise-based API for easy integration with async operations
 * 
 * Usage:
 * - alertSuccess() - For successful operations
 * - alertError() - For error notifications  
 * - alertWarning() - For warning messages
 * - confirm() - For user confirmation dialogs
 * 
 * @author Harry Potter Quiz App
 * @version 1.0.0
 */

import Swal from 'sweetalert2';

/**
 * Application-wide alert theme configuration
 * 
 * Defines consistent colors and styling for all SweetAlert2 dialogs
 * to match the Harry Potter app's dark magical aesthetic.
 */
const alertTheme = {
  background: '#0f172a',    // Dark slate background (slate-900)
  color: '#e2e8f0',         // Light text color (slate-200)  
  confirmButtonColor: '#22c55e', // Success green (emerald-500)
  cancelButtonColor: '#ef4444',  // Error red (red-500)
};

/**
 * Displays a success notification dialog
 * 
 * Used to inform users of successful operations like saving data,
 * completing quizzes, or successful authentication.
 * 
 * @function alertSuccess  
 * @param {string} title - Main heading for the success message
 * @param {string} text - Detailed success message text
 * @returns {Promise} Promise that resolves when user dismisses the alert
 * 
 * @example
 * await alertSuccess('Quiz Complete!', 'You earned 15 points for Gryffindor!');
 */
export function alertSuccess(title, text) {
  return Swal.fire({
    icon: 'success',
    title,
    text,
    background: alertTheme.background,
    color: alertTheme.color,
    confirmButtonColor: alertTheme.confirmButtonColor
  });
}

/**
 * Displays an error notification dialog
 * 
 * Used to inform users of failed operations, validation errors,
 * network failures, or other error conditions.
 * 
 * @function alertError
 * @param {string} title - Main heading for the error message  
 * @param {string} text - Detailed error message text
 * @returns {Promise} Promise that resolves when user dismisses the alert
 * 
 * @example
 * await alertError('Login Failed', 'Invalid game code or student name.');
 */
export function alertError(title, text) {
  return Swal.fire({
    icon: 'error',
    title,
    text,
    background: alertTheme.background,
    color: alertTheme.color,
    confirmButtonColor: alertTheme.confirmButtonColor
  });
}

/**
 * Displays a warning notification dialog
 * 
 * Used to inform users of potentially problematic situations that
 * require attention but aren't necessarily errors.
 * 
 * @function alertWarning
 * @param {string} title - Main heading for the warning message
 * @param {string} text - Detailed warning message text  
 * @returns {Promise} Promise that resolves when user dismisses the alert
 * 
 * @example
 * await alertWarning('Missing Information', 'Please fill in all required fields.');
 */
export function alertWarning(title, text) {
  return Swal.fire({
    icon: 'warning',
    title,
    text,
    background: alertTheme.background,
    color: alertTheme.color,
    confirmButtonColor: alertTheme.confirmButtonColor
  });
}

/**
 * Displays a confirmation dialog requiring user choice
 * 
 * Used when the user needs to confirm a potentially destructive or
 * important action before proceeding.
 * 
 * @function confirm
 * @param {string} title - Main heading for the confirmation prompt
 * @param {string} text - Detailed confirmation message text
 * @returns {Promise<{isConfirmed: boolean}>} Promise that resolves with user's choice
 * 
 * @example
 * const result = await confirm('Delete Quiz', 'This action cannot be undone.');
 * if (result.isConfirmed) {
 *   // Proceed with deletion
 * }
 */
export function confirm(title, text) {
  return Swal.fire({
    icon: 'question',
    title,
    text,
    showCancelButton: true,
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
    background: alertTheme.background,
    color: alertTheme.color,
    confirmButtonColor: alertTheme.confirmButtonColor,
    cancelButtonColor: alertTheme.cancelButtonColor,
  });
}
